import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const LS_KEY = "gs_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({
    shipping_cost: 9.99,
    free_shipping_threshold: 100,
    bundle_discount_pct: 10,
    bundle_threshold_items: 3,
  });

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (data.settings) setSettings(data.settings);
      } catch {
        /* keep defaults */
      }
    })();
  }, []);

  const addItem = (product, size, color, quantity = 1) => {
    setItems((prev) => {
      const key = `${product.id}::${size}::${color}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          size,
          color,
          quantity,
        },
      ];
    });
    setOpen(true);
  };

  const updateQty = (key, quantity) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, quantity) } : i)));
  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const totalQty = items.reduce((s, i) => s + i.quantity, 0);
    let bundleDiscount = 0;
    if (
      settings.bundle_threshold_items &&
      totalQty >= settings.bundle_threshold_items &&
      settings.bundle_discount_pct > 0
    ) {
      bundleDiscount = +(subtotal * (settings.bundle_discount_pct / 100)).toFixed(2);
    }
    const netSubtotal = subtotal - bundleDiscount;
    const freeShip = netSubtotal >= settings.free_shipping_threshold;
    const shipping = freeShip ? 0 : settings.shipping_cost;
    const total = +(netSubtotal + shipping).toFixed(2);
    const toFreeShip = Math.max(0, settings.free_shipping_threshold - netSubtotal);
    return { subtotal: +subtotal.toFixed(2), bundleDiscount, shipping, total, freeShip, toFreeShip, totalQty };
  }, [items, settings]);

  return (
    <CartContext.Provider
      value={{ items, open, setOpen, addItem, updateQty, removeItem, clear, totals, settings }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
